import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState(token ? 'working' : 'missing'); // missing | working | ok | error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | failed
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/verify-email`, { params: { token } });
        if (cancelled) return;
        setState('ok');
        setMessage(res?.data?.message || 'Email confirmed.');
      } catch (err) {
        if (cancelled) return;
        setState('error');
        setMessage(err?.response?.data?.detail || 'Could not confirm email.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Re-send a fresh verification link by email. Used when the link in the
  // user's inbox is stale (common after account deletion + re-signup, since
  // the previous user's one-time tokens get wiped along with the user doc).
  const handleResend = async (e) => {
    e?.preventDefault?.();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    setResendError('');
    try {
      await axios.post(`${API_URL}/api/users/request-email-verification`, {
        email: resendEmail.trim(),
      });
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('failed');
      setResendError(err?.response?.data?.detail || 'Could not send a fresh link. Please try again in a minute.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-[#1F3A68] mb-4">Email confirmation</h1>

          {state === 'missing' && (
            <p className="text-slate-600">This link is missing a verification token.</p>
          )}
          {state === 'working' && (
            <p className="text-slate-600">Confirming your email…</p>
          )}
          {state === 'ok' && (
            <>
              <div className="text-emerald-600 text-5xl mb-3">✓</div>
              <p className="text-emerald-800 font-semibold mb-4">{message}</p>
              <Link to="/login" className="inline-block bg-[#1F3A68] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#1F3A68]/90">Sign in</Link>
            </>
          )}
          {state === 'error' && (
            <>
              <div className="text-red-500 text-5xl mb-3">✕</div>
              <p className="text-red-700 mb-4">{message}</p>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                This usually means the link in your email is from an older signup attempt (we wipe old verification links when an account is deleted). Enter your email below and we will send you a fresh link straight away.
              </p>

              {resendStatus !== 'sent' && (
                <form onSubmit={handleResend} className="space-y-3 text-left">
                  <input
                    type="email"
                    required
                    placeholder="your-email@example.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="w-full h-11 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                    autoComplete="email"
                  />
                  {resendError && <p className="text-xs text-red-600">{resendError}</p>}
                  <button
                    type="submit"
                    disabled={resendStatus === 'sending' || !resendEmail.trim()}
                    className="w-full h-11 bg-[#1F3A68] text-white rounded-lg font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus === 'sending' ? 'Sending…' : 'Send me a fresh link'}
                  </button>
                </form>
              )}

              {resendStatus === 'sent' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 text-left mb-4">
                  Sent. Check your inbox for the latest "Confirm your Ariv email" message and click the button inside it. The new link expires in 24 hours.
                </div>
              )}

              <div className="mt-4">
                <Link to="/login" className="text-[#1F3A68] font-semibold hover:underline text-sm">Back to sign in</Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerifyEmailPage;
